import { defineStore } from 'pinia'
import type { UploadProductFiltersData } from '@/types'

// NOTE: Upload filters/state must be cleared on full browser refresh.
// We intentionally do NOT persist this store to `localStorage`.
const STORAGE_KEY = 'efashion_upload_photo_state_v1'

export interface UploadPhotoState {
  // UI state we want to persist between reloads
  currentPage: number
  itemsPerPage: number
  currentFilters: UploadProductFiltersData
  // context from route
  shootingIdFromUrl?: string
  // housekeeping
  savedAt?: number
}

const defaultState = (): UploadPhotoState => ({
  currentPage: 1,
  itemsPerPage: 25,
  currentFilters: {
    reference: '',
    idShooting: '',
    tri: 'date-desc',
    categorie: '',
    sousCategorie: '',
    sousSousCategorie: '',
    hasPhotos: '',
    premelFilter: ''
  },
  shootingIdFromUrl: undefined,
  savedAt: Date.now()
})

function loadFromStorage(): UploadPhotoState {
  // Clear any legacy persisted state (auto-clear on refresh)
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
  return defaultState()
}

function saveToStorage(_state: UploadPhotoState) {
  // Do not persist filters/state between page loads.
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export const useUploadPhotoStore = defineStore('uploadPhoto', {
  state: () => loadFromStorage(),
  actions: {
    setPage(page: number) {
      this.currentPage = page
      saveToStorage(this.$state)
    },
    setItemsPerPage(size: number) {
      this.itemsPerPage = size
      // Reset to first page to avoid empty results on smaller page size
      if (this.currentPage < 1) this.currentPage = 1
      saveToStorage(this.$state)
    },
    setFilters(filters: UploadProductFiltersData) {
      this.currentFilters = { ...filters }
      // Reset page when filters change
      this.currentPage = 1
      saveToStorage(this.$state)
    },
    patchFilters(partial: Partial<UploadProductFiltersData>) {
      this.currentFilters = { ...this.currentFilters, ...partial }
      this.currentPage = 1
      saveToStorage(this.$state)
    },
    setShootingId(id?: string) {
      this.shootingIdFromUrl = id
      saveToStorage(this.$state)
    },
    clear() {
      const d = defaultState()
      this.$state = d
      saveToStorage(this.$state)
    },
    // force a write to storage (useful after bulk mutations outside store)
    persistNow() {
      saveToStorage(this.$state)
    }
  }
})
